import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { localeForI18n } from '../../utils/locale';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Card,
  CardBody,
  Badge,
  Flex,
  useColorModeValue,
  Select,
  Grid,
  GridItem,
  Divider,
  IconButton,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  ArrowLeft,
  Clock,
  ShoppingBag,
  RefreshCw,
  Plus,
  Eye,
  Calendar,
} from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { getOrders, getActiveOrders, StoredOrder, clearAllOrders } from '../../utils/orderStorage';

interface OrderHistoryViewProps {
  onBackToMenu: () => void;
  onViewOrder: (orderNumber: string, guestPhone: string) => void;
  tableInfo: any;
}

/**
 * OrderHistoryView Component
 * Shows list of all guest's previous and current orders
 */
const OrderHistoryView: React.FC<OrderHistoryViewProps> = ({
  onBackToMenu,
  onViewOrder,
  tableInfo
}) => {
  const { t, i18n } = useTranslation();
  const locale = localeForI18n(i18n.language);
  const [orders, setOrders] = useState<StoredOrder[]>([]);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [isLoading, setIsLoading] = useState(false);
  
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  // Load orders on mount
  useEffect(() => {
    loadOrders();
  }, [filter]);

  const loadOrders = () => {
    setIsLoading(true);
    try {
      let loadedOrders: StoredOrder[] = [];
      
      switch (filter) {
        case 'active':
          loadedOrders = getActiveOrders();
          break;
        case 'completed':
          const allOrders = getOrders();
          const completedStatuses = ['completed', 'cancelled'];
          loadedOrders = allOrders.filter(order => completedStatuses.includes(order.status));
          break;
        default:
          loadedOrders = getOrders();
      }
      
      setOrders(loadedOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast({
        title: t('tableOrdering.history.errorTitle'),
        description: t('tableOrdering.history.loadFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    const colorMap: { [key: string]: string } = {
      pending: 'yellow', confirmed: 'blue', preparing: 'orange',
      ready: 'green', served: 'purple', completed: 'gray', cancelled: 'red'
    };
    if (colorMap[status]) {
      return { label: t(`tableOrdering.history.statuses.${status}`), color: colorMap[status] };
    }
    return { label: status, color: 'gray' };
  };

  const formatOrderTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 1) {
      const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      return t('tableOrdering.history.minutesAgo', { count: diffMinutes });
    } else if (diffHours < 24) {
      return t('tableOrdering.history.hoursAgo', { count: diffHours });
    } else {
      return date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const handleViewOrder = (order: StoredOrder) => {
    onViewOrder(order.order_number, order.guest_phone);
  };

  const handleClearHistory = () => {
    if (window.confirm(t('tableOrdering.history.clearConfirm'))) {
      clearAllOrders();
      setOrders([]);
      toast({
        title: t('tableOrdering.history.historyCleared'),
        description: t('tableOrdering.history.historyClearedDesc'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const activeCount = orders.filter(order => 
    ['pending', 'confirmed', 'preparing', 'ready', 'served'].includes(order.status)
  ).length;

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="4xl" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Button 
                leftIcon={<Icon as={ArrowLeft} />} 
                variant="ghost" 
                onClick={onBackToMenu}
              >
                {t('tableOrdering.history.backToMenu')}
              </Button>
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold" color="gray.800">
                  {t('tableOrdering.history.myOrders')}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {t('tableOrdering.history.totalOrders', { count: orders.length })}
                  {activeCount > 0 && t('tableOrdering.history.activeSuffix', { count: activeCount })}
                </Text>
              </VStack>
            </HStack>
            
            <HStack spacing={2}>
              <IconButton
                aria-label={t('tableOrdering.history.refreshAria')}
                icon={<Icon as={RefreshCw} />}
                variant="outline"
                size="sm"
                onClick={loadOrders}
                isLoading={isLoading}
              />
              <Button 
                leftIcon={<Icon as={Plus} />}
                colorScheme="blue"
                onClick={onBackToMenu}
              >
                {t('tableOrdering.history.newOrder')}
              </Button>
            </HStack>
          </Flex>

          {/* Filter Controls */}
          <Card bg={cardBg}>
            <CardBody p={4}>
              <HStack justify="space-between" align="center">
                <HStack spacing={4}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    {t('tableOrdering.history.filter')}
                  </Text>
                  <Select
                    size="sm"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as any)}
                    width="150px"
                  >
                    <option value="all">{t('tableOrdering.history.filterAll')}</option>
                    <option value="active">{t('tableOrdering.history.filterActive')}</option>
                    <option value="completed">{t('tableOrdering.history.filterCompleted')}</option>
                  </Select>
                </HStack>
                
                {orders.length > 0 && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    colorScheme="red"
                    onClick={handleClearHistory}
                  >
                    {t('tableOrdering.history.clearHistory')}
                  </Button>
                )}
              </HStack>
            </CardBody>
          </Card>

          {/* Orders List */}
          {orders.length === 0 ? (
            <Card bg={cardBg}>
              <CardBody py={12} textAlign="center">
                <VStack spacing={4}>
                  <Icon as={ShoppingBag} boxSize={12} color="gray.400" />
                  <VStack spacing={2}>
                    <Text fontSize="lg" fontWeight="medium" color="gray.600">
                      {filter === 'active' ? t('tableOrdering.history.noActiveTitle') : t('tableOrdering.history.noHistoryTitle')}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {filter === 'active'
                        ? t('tableOrdering.history.noActiveBody')
                        : t('tableOrdering.history.noHistoryBody')
                      }
                    </Text>
                  </VStack>
                  <Button 
                    leftIcon={<Icon as={Plus} />}
                    colorScheme="blue"
                    onClick={onBackToMenu}
                  >
                    {t('tableOrdering.history.placeFirstOrder')}
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          ) : (
            <VStack spacing={4} align="stretch">
              {orders.map((order, index) => {
                const statusInfo = getStatusInfo(order.status);
                const isActive = ['pending', 'confirmed', 'preparing', 'ready', 'served'].includes(order.status);
                
                return (
                  <Card key={order.order_number} bg={cardBg} variant="outline">
                    <CardBody p={4}>
                      <Grid templateColumns={{ base: '1fr', md: '1fr 200px 120px' }} gap={4} alignItems="center">
                        {/* Order Info */}
                        <GridItem>
                          <VStack align="start" spacing={2}>
                            <HStack spacing={3} wrap="wrap">
                              <Text fontWeight="bold" fontSize="lg" color="blue.600">
                                #{order.order_number}
                              </Text>
                              <Badge 
                                colorScheme={statusInfo.color}
                                px={3}
                                py={1}
                                borderRadius="full"
                              >
                                {statusInfo.label}
                              </Badge>
                              {isActive && (
                                <Badge colorScheme="green" variant="outline">
                                  {t('tableOrdering.history.active')}
                                </Badge>
                              )}
                            </HStack>
                            
                            <VStack align="start" spacing={1}>
                              <HStack spacing={4} fontSize="sm" color="gray.600">
                                <Text>{order.restaurant_name}</Text>
                                <Text>•</Text>
                                <Text>{t('tableOrdering.history.tableLabel', { number: order.table_number })}</Text>
                                <Text>•</Text>
                                <Text>{t('tableOrdering.history.itemsCount', { count: order.items.length })}</Text>
                              </HStack>
                              
                              <HStack spacing={2} fontSize="sm" color="gray.500">
                                <Icon as={Clock} boxSize={3} />
                                <Text>{formatOrderTime(order.timestamp)}</Text>
                              </HStack>
                            </VStack>
                          </VStack>
                        </GridItem>

                        {/* Amount */}
                        <GridItem>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.600">{t('tableOrdering.history.totalAmount')}</Text>
                            <Text fontSize="lg" fontWeight="bold" color="gray.800">
                              {formatCurrency(Number(order.total_amount), tableInfo?.hotel?.currency || 'USD')}
                            </Text>
                          </VStack>
                        </GridItem>

                        {/* Action */}
                        <GridItem>
                          <Button
                            leftIcon={<Icon as={Eye} />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleViewOrder(order)}
                            width="100%"
                          >
                            {t('tableOrdering.history.viewDetails')}
                          </Button>
                        </GridItem>
                      </Grid>
                    </CardBody>
                  </Card>
                );
              })}
            </VStack>
          )}

          {/* Summary Card */}
          {orders.length > 0 && (
            <Card bg={cardBg} variant="outline">
              <CardBody>
                <HStack justify="space-between" align="center">
                  <HStack spacing={4}>
                    <Icon as={Calendar} color="blue.500" />
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{t('tableOrdering.history.summary')}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {t('tableOrdering.history.summaryLine', { total: orders.length, active: activeCount })}
                      </Text>
                    </VStack>
                  </HStack>
                  
                  <Text fontSize="lg" fontWeight="bold" color="blue.600">
                    {formatCurrency(
                      orders.reduce((sum, order) => sum + Number(order.total_amount), 0),
                      tableInfo?.hotel?.currency || 'USD'
                    )}
                  </Text>
                </HStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default OrderHistoryView;